import React, { useState, useEffect } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '../../components/common/Button';
import Loading from '../../components/common/Loading';
import TabPanel from '../../components/common/TabPanel';
import './EventsPage.css';
import { format } from 'date-fns';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextareaAutosize from '@mui/material/TextareaAutosize';

interface Event {
  id: number;
  user_id: string;
  action: string;
  detail?: string;
  value: number;
  datetime: string;
  account_type: string;
  metadata: any;
}

interface Program {
  id: number;
  name: string;
  value_paid: string;
  date_start: string;
  date_end: string | null;
}

interface UserEnrollment {
  id: number;
  user_id: string;
  program_id: number;
  enrollment_date: string;
  status: string;
  completion_date: string | null;
  reward_paid: boolean;
  reward_amount: string | null;
  reward_date: string | null;
  Program: Program;
}

interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

const EventsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [events, setEvents] = useState<Event[]>([]);
  const [enrollments, setEnrollments] = useState<UserEnrollment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [enrollmentsError, setEnrollmentsError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    limit: 10,
    offset: 0,
    hasMore: false
  });
  const [enrollmentsPagination, setEnrollmentsPagination] = useState<PaginationInfo>({
    total: 0,
    limit: 10,
    offset: 0,
    hasMore: false
  });
  const [userFilter, setUserFilter] = useState<string>('');
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedEnrollment, setSelectedEnrollment] = useState<UserEnrollment | null>(null);
  const [isEventFormOpen, setIsEventFormOpen] = useState<boolean>(false);
  const [eventFormMode, setEventFormMode] = useState<'create' | 'edit'>('create');
  const [eventFormData, setEventFormData] = useState<Partial<Event>>({
    user_id: '',
    action: '',
    detail: '',
    value: 0,
    account_type: ''
  });
  const [availableActions, setAvailableActions] = useState<{ action: string, details: string[] }[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [eventSaveSuccess, setEventSaveSuccess] = useState<string | null>(null);

  // Get the API base URL from environment variables or use a default
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 0 && events.length === 0) {
      fetchEvents();
    } else if (newValue === 1 && enrollments.length === 0) {
      fetchEnrollments();
    }
  };

  const fetchEvents = async (offset = 0, limit = 10, userId = '') => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/api/events?limit=${limit}&offset=${offset}`;
      
      if (userId) {
        url = `${API_BASE_URL}/api/events/user/${userId}?limit=${limit}&offset=${offset}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      
      const data = await response.json();
      setEvents(data.events);
      setPagination(data.pagination);
      setError(null);
    } catch (err) {
      setError('Failed to fetch events. Please try again later.');
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrollments = async (offset = 0, limit = 10, userId = '') => {
    setEnrollmentsLoading(true);
    try {
      let url = `${API_BASE_URL}/api/user-enrollments?limit=${limit}&offset=${offset}`;
      
      if (userId) {
        url = `${API_BASE_URL}/api/user-enrollments/${userId}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (userId) {
        // When fetching for a specific user, the response is an array
        setEnrollments(data);
        setEnrollmentsPagination({
          total: data.length,
          limit,
          offset: 0,
          hasMore: false
        });
      } else {
        // When fetching all enrollments, the response has pagination
        setEnrollments(data.enrollments);
        setEnrollmentsPagination(data.pagination);
      }
      
      setEnrollmentsError(null);
    } catch (err) {
      setEnrollmentsError('Failed to fetch enrollments. Please try again later.');
      console.error('Error fetching enrollments:', err);
    } finally {
      setEnrollmentsLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents(pagination.offset, pagination.limit, userFilter);
  }, []);

  useEffect(() => {
    if (events.length > 0) {
      const actionsMap = new Map();
      
      events.forEach(event => {
        if (!actionsMap.has(event.action)) {
          actionsMap.set(event.action, new Set());
        }
        
        if (event.detail) {
          actionsMap.get(event.action).add(event.detail);
        }
      });
      
      // Create array of actions with their details
      const actionsArray = Array.from(actionsMap.entries()).map(([action, detailsSet]) => ({
        action,
        details: Array.from(detailsSet) as string[]
      }));
      
      // Merge with predefined actions
      const mergedActions = [...actionsArray];
      
      const predefinedActions = [
        { action: 'saved', details: ['checking', 'savings', 'investment'] },
        { action: 'created_goal', details: ['retirement', 'vacation', 'education', 'home'] },
        { action: 'talked_to', details: ['advisor', 'customer service', 'partner'] },
        { action: 'joined', details: ['membership', 'program', 'webinar', 'newsletter'] },
        { action: 'completed', details: ['survey', 'onboarding', 'profile', 'training'] }
      ];
      
      predefinedActions.forEach(predefined => {
        const existing = mergedActions.find(a => a.action === predefined.action);
        if (existing) {
          // Add any predefined details not already in the list
          predefined.details.forEach(detail => {
            if (!existing.details.includes(detail)) {
              existing.details.push(detail);
            }
          });
        } else {
          // Add the entire predefined action
          mergedActions.push(predefined);
        }
      });
      
      setAvailableActions(mergedActions);
    } else {
      setAvailableActions([
        { action: 'saved', details: ['checking', 'savings', 'investment'] },
        { action: 'created_goal', details: ['retirement', 'vacation', 'education', 'home'] },
        { action: 'talked_to', details: ['advisor', 'customer service', 'partner'] },
        { action: 'joined', details: ['membership', 'program', 'webinar', 'newsletter'] },
        { action: 'completed', details: ['survey', 'onboarding', 'profile', 'training'] }
      ]);
    }
  }, [events]);

  const handleNextPage = () => {
    const newOffset = pagination.offset + pagination.limit;
    fetchEvents(newOffset, pagination.limit, userFilter);
  };

  const handlePrevPage = () => {
    const newOffset = Math.max(0, pagination.offset - pagination.limit);
    fetchEvents(newOffset, pagination.limit, userFilter);
  };

  const handleEnrollmentsNextPage = () => {
    const newOffset = enrollmentsPagination.offset + enrollmentsPagination.limit;
    fetchEnrollments(newOffset, enrollmentsPagination.limit, userFilter);
  };

  const handleEnrollmentsPrevPage = () => {
    const newOffset = Math.max(0, enrollmentsPagination.offset - enrollmentsPagination.limit);
    fetchEnrollments(newOffset, enrollmentsPagination.limit, userFilter);
  };

  const handleUserFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserFilter(e.target.value);
  };

  const handleFilterApply = () => {
    if (userFilter) {
      setPagination({
        total: 0,
        limit: 10,
        offset: 0,
        hasMore: false
      });
      fetchEvents(0, 10, userFilter);
    }
  };

  const handleEnrollmentsFilterApply = () => {
    if (userFilter) {
      setEnrollmentsPagination({
        total: 0,
        limit: 10,
        offset: 0,
        hasMore: false
      });
      fetchEnrollments(0, 10, userFilter);
    }
  };

  const handleClearFilter = () => {
    setUserFilter('');
    setPagination({
      total: 0,
      limit: 10,
      offset: 0,
      hasMore: false
    });
    setEnrollmentsPagination({
      total: 0,
      limit: 10,
      offset: 0,
      hasMore: false
    });
    tabValue === 0 ? fetchEvents() : fetchEnrollments();
  };

  const handleRowClick = (event: Event) => {
    setSelectedEvent(event);
  };

  const handleEnrollmentRowClick = (enrollment: UserEnrollment) => {
    setSelectedEnrollment(enrollment);
  };

  const handleCloseDetails = () => {
    setSelectedEvent(null);
  };

  const handleCloseEnrollmentDetails = () => {
    setSelectedEnrollment(null);
  };

  const formatDateTime = (dateTimeStr: string) => {
    try {
      return format(new Date(dateTimeStr), 'MMM d, yyyy h:mm a');
    } catch (e) {
      return dateTimeStr;
    }
  };

  // Custom chip components for event actions and status
  const EventActionChip = ({ action }: { action: string }) => {
    const getActionClass = () => {
      switch (action) {
        case 'purchase':
          return 'purchase';
        case 'solar_installation':
          return 'solar_installation';
        case 'account_creation':
          return 'account_creation';
        default:
          return 'default';
      }
    };

    return (
      <span className={`event-chip ${getActionClass()}`}>
        {action}
      </span>
    );
  };

  const StatusChip = ({ status }: { status: string }) => {
    const getStatusClass = () => {
      switch (status.toLowerCase()) {
        case 'active':
          return 'active';
        case 'completed':
          return 'completed';
        case 'pending':
          return 'pending';
        case 'cancelled':
          return 'cancelled';
        default:
          return 'default';
      }
    };

    return (
      <span className={`status-chip ${getStatusClass()}`}>
        {status}
      </span>
    );
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'completed':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  // Add handlers for the event form
  const handleOpenCreateEventForm = () => {
    setEventFormMode('create');
    setEventFormData({
      user_id: userFilter || '', // Pre-fill with filtered user if available
      action: '',
      detail: '',
      value: 0,
      account_type: ''
    });
    setIsEventFormOpen(true);
  };

  const handleOpenEditEventForm = (event: Event) => {
    setEventFormMode('edit');
    setEventFormData({
      ...event
    });
    setIsEventFormOpen(true);
  };

  const handleCloseEventForm = () => {
    setIsEventFormOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'value') {
      // Parse numeric value
      setEventFormData({
        ...eventFormData,
        [name]: parseFloat(value) || 0
      });
    } else {
      setEventFormData({
        ...eventFormData,
        [name]: value
      });
    }
  };

  const handleActionChange = (e: SelectChangeEvent) => {
    setEventFormData({
      ...eventFormData,
      action: e.target.value,
      detail: '' // Reset detail when action changes
    });
  };

  const handleDetailChange = (e: SelectChangeEvent) => {
    setEventFormData({
      ...eventFormData,
      detail: e.target.value
    });
  };

  const handleSaveEvent = async () => {
    if (!eventFormData.user_id || !eventFormData.action) {
      alert('User ID and Action are required fields.');
      return;
    }
    
    setIsSaving(true);
    setEventSaveSuccess(null);
    
    try {
      const method = eventFormMode === 'create' ? 'POST' : 'PUT';
      const url = eventFormMode === 'create' 
        ? `${API_BASE_URL}/api/events`
        : `${API_BASE_URL}/api/events/${eventFormData.id}`;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...eventFormData,
          datetime: eventFormData.datetime || new Date().toISOString()
        })
      });
      
      const data = await response.json();
      console.log('Event saved response:', data);
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP error! Status: ${response.status}`);
      }
      
      // Display success message
      let successMessage = 'Event saved successfully!';
      
      // Check if there was a processing error
      if (data.processingError) {
        successMessage = `Event saved, but some processing had issues: ${data.processingError}`;
        console.warn('Event processing had issues:', data.processingError);
      }
      // Check if any programs were triggered
      else if (data.triggeredPrograms && data.triggeredPrograms.length > 0) {
        const programNames = data.triggeredPrograms
          .filter((p: any) => !p.enrollmentError) // Filter out programs with enrollment errors
          .map((p: any) => p.program_name)
          .join(', ');
        
        if (programNames) {
          successMessage += ` User ${eventFormData.user_id} is now eligible for: ${programNames}`;
          console.log(`User ${eventFormData.user_id} is eligible for programs: ${programNames}`);
        }
        
        // Check if there were any enrollment errors
        const programsWithErrors = data.triggeredPrograms
          .filter((p: any) => p.enrollmentError)
          .map((p: any) => p.program_name);
        
        if (programsWithErrors.length > 0) {
          console.warn(`Problems enrolling in programs: ${programsWithErrors.join(', ')}`);
          successMessage += ` (Note: Had issues enrolling in some programs)`;
        }
      }
      
      setEventSaveSuccess(successMessage);
      
      // Refresh events list
      fetchEvents(pagination.offset, pagination.limit, userFilter);
      
      // Close the form after a delay to show the success message
      setTimeout(() => {
        handleCloseEventForm();
        setEventSaveSuccess(null);
      }, 2000);
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Failed to save event. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container className="events-page">
      <div className="page-header">
        <Typography variant="h4" className="page-title">
          Events Explorer
        </Typography>
        <div className="header-actions">
          <Button 
            onClick={handleOpenCreateEventForm}
            label="Add Event"
            color="primary"
          />
        </div>
      </div>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          aria-label="events tabs"
        >
          <Tab label="User Events" id="simple-tab-0" aria-controls="simple-tabpanel-0" />
          <Tab label="Program Enrollments" id="simple-tab-1" aria-controls="simple-tabpanel-1" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <div className="filter-container">
          <TextField
            label="Filter by User ID"
            variant="outlined"
            value={userFilter}
            onChange={handleUserFilterChange}
            className="user-filter"
            size="small"
          />
          <Button 
            onClick={handleFilterApply}
            label="Apply Filter"
            color="primary"
          />
          {userFilter && (
            <Button 
              onClick={handleClearFilter}
              label="Clear Filter"
              color="gray"
            />
          )}
        </div>

        {loading ? (
          <Box className="loading-container">
            <Loading />
          </Box>
        ) : error ? (
          <Paper className="error-container">
            <Typography>{error}</Typography>
          </Paper>
        ) : (
          <Box className="events-container">
            <TableContainer component={Paper} className="table-container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>User ID</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Detail</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>Account Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.length > 0 ? (
                    events.map((event) => (
                      <TableRow 
                        key={event.id} 
                        onClick={() => handleRowClick(event)}
                        className="event-row"
                        hover
                      >
                        <TableCell>{event.id}</TableCell>
                        <TableCell>{event.user_id}</TableCell>
                        <TableCell>
                          <EventActionChip action={event.action} />
                        </TableCell>
                        <TableCell>{event.detail || '-'}</TableCell>
                        <TableCell>{event.value !== null ? `$${event.value.toFixed(2)}` : 'N/A'}</TableCell>
                        <TableCell>{formatDateTime(event.datetime)}</TableCell>
                        <TableCell>{event.account_type}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No events found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {!userFilter && (
              <Box className="pagination-controls">
                <Button 
                  onClick={handlePrevPage}
                  label="Previous"
                  color="gray"
                  className={pagination.offset === 0 || loading ? 'disabled-button' : ''}
                />
                <Typography variant="body2" className="pagination-info">
                  Showing {pagination.offset + 1} - {Math.min(pagination.offset + events.length, pagination.total)} of {pagination.total}
                </Typography>
                <Button 
                  onClick={handleNextPage}
                  label="Next"
                  color="gray"
                  className={!pagination.hasMore || loading ? 'disabled-button' : ''}
                />
              </Box>
            )}
          </Box>
        )}
        
        {selectedEvent && (
          <div className="event-details">
            <div className="event-details-header">
              <h3>Event Details</h3>
              <Button 
                onClick={handleCloseDetails}
                label="Close"
                color="gray"
                className="close-button"
              />
            </div>
            <div className="event-details-content">
              <div className="detail-item">
                <span className="detail-label">Event ID</span>
                <span className="detail-value">{selectedEvent.id}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">User ID</span>
                <span className="detail-value">{selectedEvent.user_id}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Action</span>
                <span className="detail-value">
                  <EventActionChip action={selectedEvent.action} />
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Detail</span>
                <span className="detail-value">
                  {selectedEvent.detail || 'None'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Value</span>
                <span className="detail-value">
                  {selectedEvent.value !== null ? `$${selectedEvent.value.toFixed(2)}` : 'N/A'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Date/Time</span>
                <span className="detail-value">{formatDateTime(selectedEvent.datetime)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Account Type</span>
                <span className="detail-value">{selectedEvent.account_type}</span>
              </div>
              
              <div className="metadata-section">
                <Typography variant="h6" className="metadata-header">Metadata</Typography>
                <pre className="metadata-json">
                  {JSON.stringify(selectedEvent.metadata, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <div className="filter-container">
          <TextField
            label="Filter by User ID"
            variant="outlined"
            value={userFilter}
            onChange={handleUserFilterChange}
            className="user-filter"
            size="small"
          />
          <Button 
            onClick={handleEnrollmentsFilterApply}
            label="Apply Filter"
            color="primary"
          />
          {userFilter && (
            <Button 
              onClick={handleClearFilter}
              label="Clear Filter"
              color="gray"
            />
          )}
        </div>
        
        {enrollmentsLoading ? (
          <Box className="loading-container">
            <Loading />
          </Box>
        ) : enrollmentsError ? (
          <Paper className="error-container">
            <Typography>{enrollmentsError}</Typography>
          </Paper>
        ) : (
          <Box className="events-container">
            <TableContainer component={Paper} className="table-container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>User ID</TableCell>
                    <TableCell>Program</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Enrollment Date</TableCell>
                    <TableCell>Reward Paid</TableCell>
                    <TableCell>Reward Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {enrollments.length > 0 ? (
                    enrollments.map((enrollment) => (
                      <TableRow 
                        key={enrollment.id} 
                        onClick={() => handleEnrollmentRowClick(enrollment)}
                        className="event-row"
                        hover
                      >
                        <TableCell>{enrollment.id}</TableCell>
                        <TableCell>{enrollment.user_id}</TableCell>
                        <TableCell>{enrollment.Program.name}</TableCell>
                        <TableCell>
                          <StatusChip status={enrollment.status} />
                        </TableCell>
                        <TableCell>{formatDateTime(enrollment.enrollment_date)}</TableCell>
                        <TableCell>{enrollment.reward_paid ? 'Yes' : 'No'}</TableCell>
                        <TableCell>{enrollment.reward_amount ? `$${enrollment.reward_amount}` : 'N/A'}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No enrollments found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {!userFilter && (
              <Box className="pagination-controls">
                <Button 
                  onClick={handleEnrollmentsPrevPage}
                  label="Previous"
                  color="gray"
                  className={enrollmentsPagination.offset === 0 || enrollmentsLoading ? 'disabled-button' : ''}
                />
                <Typography variant="body2" className="pagination-info">
                  Showing {enrollmentsPagination.offset + 1} - {Math.min(enrollmentsPagination.offset + enrollments.length, enrollmentsPagination.total)} of {enrollmentsPagination.total}
                </Typography>
                <Button 
                  onClick={handleEnrollmentsNextPage}
                  label="Next"
                  color="gray"
                  className={!enrollmentsPagination.hasMore || enrollmentsLoading ? 'disabled-button' : ''}
                />
              </Box>
            )}
          </Box>
        )}
        
        {selectedEnrollment && (
          <div className="event-details">
            <div className="event-details-header">
              <h3>Enrollment Details</h3>
              <Button 
                onClick={handleCloseEnrollmentDetails}
                label="Close"
                color="gray"
                className="close-button"
              />
            </div>
            <div className="event-details-content">
              <div className="detail-item">
                <span className="detail-label">Enrollment ID</span>
                <span className="detail-value">{selectedEnrollment.id}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">User ID</span>
                <span className="detail-value">{selectedEnrollment.user_id}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Program</span>
                <span className="detail-value">{selectedEnrollment.Program.name}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Status</span>
                <span className="detail-value">
                  <StatusChip status={selectedEnrollment.status} />
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Enrollment Date</span>
                <span className="detail-value">{formatDateTime(selectedEnrollment.enrollment_date)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Reward Paid</span>
                <span className="detail-value">{selectedEnrollment.reward_paid ? 'Yes' : 'No'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Reward Amount</span>
                <span className="detail-value">
                  {selectedEnrollment.reward_amount ? `$${selectedEnrollment.reward_amount}` : 'N/A'}
                </span>
              </div>
              {selectedEnrollment.completion_date && (
                <div className="detail-item">
                  <span className="detail-label">Completion Date</span>
                  <span className="detail-value">{formatDateTime(selectedEnrollment.completion_date)}</span>
                </div>
              )}
              {selectedEnrollment.reward_date && (
                <div className="detail-item">
                  <span className="detail-label">Reward Date</span>
                  <span className="detail-value">{formatDateTime(selectedEnrollment.reward_date)}</span>
                </div>
              )}
              <div className="detail-item">
                <span className="detail-label">Program Start</span>
                <span className="detail-value">{formatDateTime(selectedEnrollment.Program.date_start)}</span>
              </div>
              {selectedEnrollment.Program.date_end && (
                <div className="detail-item">
                  <span className="detail-label">Program End</span>
                  <span className="detail-value">{formatDateTime(selectedEnrollment.Program.date_end)}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </TabPanel>

      {/* Event Form Dialog */}
      <Dialog open={isEventFormOpen} onClose={handleCloseEventForm} maxWidth="md" fullWidth>
        <DialogTitle>
          {eventFormMode === 'create' ? 'Create New Event' : 'Edit Event'}
        </DialogTitle>
        <DialogContent>
          <div className="event-form">
            {eventSaveSuccess && (
              <div className="success-message">
                {eventSaveSuccess}
              </div>
            )}
            <div className="form-row">
              <TextField
                label="User ID"
                name="user_id"
                value={eventFormData.user_id || ''}
                onChange={handleInputChange}
                fullWidth
                required
                margin="normal"
              />
            </div>
            
            <div className="form-row">
              <FormControl fullWidth margin="normal">
                <InputLabel id="action-label">Action</InputLabel>
                <Select
                  labelId="action-label"
                  name="action"
                  value={eventFormData.action || ''}
                  onChange={handleActionChange}
                  required
                >
                  <MenuItem value="">
                    <em>Select an action</em>
                  </MenuItem>
                  {availableActions.map((actionOption) => (
                    <MenuItem key={actionOption.action} value={actionOption.action}>
                      {actionOption.action}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            
            <div className="form-row">
              <FormControl fullWidth margin="normal" disabled={!eventFormData.action}>
                <InputLabel id="detail-label">Detail</InputLabel>
                <Select
                  labelId="detail-label"
                  name="detail"
                  value={eventFormData.detail || ''}
                  onChange={handleDetailChange}
                >
                  <MenuItem value="">
                    <em>Select a detail</em>
                  </MenuItem>
                  {eventFormData.action && availableActions.find(a => a.action === eventFormData.action)?.details.map((detail) => (
                    <MenuItem key={detail} value={detail}>
                      {detail}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            
            <div className="form-row">
              <TextField
                label="Value"
                name="value"
                type="number"
                value={eventFormData.value || 0}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
              />
            </div>
            
            <div className="form-row">
              <TextField
                label="Account Type"
                name="account_type"
                value={eventFormData.account_type || ''}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
              />
            </div>
            
            {eventFormMode === 'edit' && (
              <div className="form-row">
                <TextField
                  label="Event Date"
                  type="datetime-local"
                  name="datetime"
                  value={eventFormData.datetime 
                    ? new Date(eventFormData.datetime).toISOString().slice(0, 16) 
                    : new Date().toISOString().slice(0, 16)}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </div>
            )}
            
            {eventFormMode === 'edit' && (
              <div className="form-row">
                <FormControl fullWidth margin="normal">
                  <InputLabel id="metadata-label">Metadata</InputLabel>
                  <TextareaAutosize
                    aria-label="metadata"
                    minRows={3}
                    placeholder="Metadata in JSON format"
                    style={{ width: '100%', padding: '8px' }}
                    value={
                      eventFormData.metadata 
                        ? (typeof eventFormData.metadata === 'string' 
                          ? eventFormData.metadata 
                          : JSON.stringify(eventFormData.metadata, null, 2))
                        : ''
                    }
                    onChange={(e) => {
                      try {
                        const metadata = e.target.value ? JSON.parse(e.target.value) : {};
                        setEventFormData({
                          ...eventFormData,
                          metadata
                        });
                      } catch (err) {
                        // Allow invalid JSON during editing, it will be validated on save
                        setEventFormData({
                          ...eventFormData,
                          metadata: e.target.value
                        });
                      }
                    }}
                  />
                </FormControl>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseEventForm} 
            label="Cancel" 
            color="gray"
          />
          <Button
            onClick={handleSaveEvent}
            label={isSaving ? 'Saving...' : 'Save'}
            color="primary"
            disabled={isSaving}
          />
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EventsPage;